<script setup lang="ts">
import { computed, nextTick, ref, useSlots, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { canSaveFile } from '@satellite/helpers/downloadFile';

defineOptions({ name: 'PreviewDialog' });

const {
  modelValue,
  fileName = '',
  maxWidth = '1080px',
  closeOnScrim = true,
  showDownload = false,
  isLoading = false,
  fillsViewport = false,
  testId = 'preview'
} = defineProps<{
  /**
   * Whether the preview is open.
   */
  modelValue: boolean;
  /**
   * Shown in the header bar.
   */
  fileName?: string;
  maxWidth?: string | number;
  closeOnScrim?: boolean;
  /**
   * Renders the download action; the dialog only reports it through `download`.
   */
  showDownload?: boolean;
  /**
   * Holds the dialog at a readable size until there is something to show, so it does not
   * open at the size of a spinner and then snap to the content.
   */
  isLoading?: boolean;
  /**
   * Keeps the dialog at a constant height whatever it holds, for content that scrolls
   * rather than fitting — a document reader, not a photo.
   */
  fillsViewport?: boolean;
  /**
   * Prefix for every `data-testid` this dialog renders, e.g. `<testId>-close`.
   */
  testId?: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  close: [];
  download: [];
  previous: [];
  next: [];
}>();

const { t } = useI18n();
const slots = useSlots();

const rootRef = ref<HTMLElement | null>(null);

const isOpen = computed({
  get: () => modelValue,
  set: value => emit('update:modelValue', value)
});

// Not offered where the file cannot be saved without taking the app off screen.
const isDownloadOffered = computed(() => showDownload && canSaveFile());

// Plain functions, not computeds: slot presence is not reactive, and both bars are
// skipped entirely when nothing was asked of them.
function hasHeader() {
  return (
    Boolean(slots.header) ||
    Boolean(slots['header-actions']) ||
    Boolean(fileName) ||
    isDownloadOffered.value
  );
}

function hasFooter() {
  return Boolean(slots.footer);
}

function close() {
  isOpen.value = false;
  emit('close');
}

watch(
  isOpen,
  value => {
    if (value) {
      nextTick(() => rootRef.value?.focus());
    }
  },
  // Also covers being mounted already open, not just opened later.
  { immediate: true }
);
</script>

<template>
  <v-dialog
    v-model="isOpen"
    :max-width="maxWidth"
    :persistent="!closeOnScrim"
    :data-testid="`${testId}-dialog`"
    @click:outside="closeOnScrim && close()">
    <div
      ref="rootRef"
      class="preview-dialog"
      :class="{
        'preview-dialog--loading': isLoading && !fillsViewport,
        'preview-dialog--filled': fillsViewport
      }"
      tabindex="0"
      @keydown.left.prevent="emit('previous')"
      @keydown.right.prevent="emit('next')"
      @keydown.esc.prevent="close">
      <div
        v-if="hasHeader()"
        class="preview-dialog__bar d-flex align-center justify-space-between p-x-4 p-y-2"
        :data-testid="`${testId}-header`">
        <!-- @slot Replaces the header bar contents. -->
        <slot name="header">
          <span class="typography-body-md text-truncate" :data-testid="`${testId}-file-name`">
            {{ fileName }}
          </span>

          <div class="d-flex align-center gap-2">
            <!-- @slot Extra actions, rendered before the built-in ones. -->
            <slot name="header-actions" />

            <v-btn
              v-if="isDownloadOffered"
              icon="mdi-download"
              variant="text"
              density="comfortable"
              :aria-label="t('global.basicTerms.download')"
              :data-testid="`${testId}-download`"
              @click="emit('download')" />
            <v-btn
              icon="mdi-close"
              variant="text"
              density="comfortable"
              :aria-label="t('global.basicTerms.close')"
              :data-testid="`${testId}-close`"
              @click="close" />
          </div>
        </slot>
      </div>

      <!-- @slot What is being previewed. -->
      <slot />

      <div
        v-if="hasFooter()"
        class="preview-dialog__bar d-flex align-center justify-center gap-4 p-y-2"
        :data-testid="`${testId}-footer`">
        <!-- @slot Footer bar contents. -->
        <slot name="footer" />
      </div>
    </div>
  </v-dialog>
</template>

<style lang="scss" scoped>
.preview-dialog {
  display: flex;
  max-height: 90vh;
  // Landscape on iOS: `vh` measures the viewport with the browser UI hidden, so a
  // `vh` cap alone leaves the content taller than the space actually on screen.
  max-height: 90dvh;
  flex-direction: column;
  outline: none;
  overflow: hidden;
}

/* Sized so a preview opens at roughly the shape it will settle into. */
.preview-dialog--loading {
  min-width: min(90vw, 560px);
  min-height: 60dvh;
}

/* The bars take what they need and the content scrolls in the rest, at every stage. */
.preview-dialog--filled {
  height: 90vh;
  height: 90dvh;
  min-width: min(90vw, 560px);
}

.preview-dialog__bar {
  // Never squeezed by what is being previewed: the bars carry the only way out.
  flex: 0 0 auto;
  background-color: rgba(0, 0, 0, 0.72);
  color: $color-neutral-0;
}
</style>
